<template>
  <ul class="facets-group default">
    <li v-for="facet of facets" :key="facet.slug">
      <base-checkbox
        :name="facet.slug"
        :model-value="selectedFacets.includes(facet.slug)"
        :disabled="!isFacetActive(facet.slug)"
        @update:model-value="$emit('facetsChange', [[facet.slug, $event]])"
      >
        {{ facet.name }}
        <span v-show="shouldShowCount(facet.slug)" class="count">
          {{ counts[facet.slug] }}
        </span>
      </base-checkbox>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'

interface Facet {
  name: string
  slug: string
}

interface FacetGroup {
  name: string
  slug: string
  facets: Facet[]
  hideEmpty: boolean
}

const props = defineProps<{
  group: FacetGroup
  counts: Record<string, number>
  initialFacetCounts: Record<string, number>
  selectedFacets: string[]
}>()

const searchWizardStore = useSearchWizardStore(usePinia())
const route = useRoute()
const lang = computed(() => (route.params.lang as string) || 'en-gb')
const subcategoryFacetSlugs = computed(
  () => getCampsiteCategoriesPresenter(lang.value).subcategoryFacetSlugs || [],
)

const isSubcategoryFacet = (slug: string) => {
  return subcategoryFacetSlugs.value.includes(slug)
}

const isAnotherSubcategoryFacetSelected = (currentSlug: string) => {
  return props.selectedFacets.some(
    (slug) => isSubcategoryFacet(slug) && slug !== currentSlug,
  )
}

function shouldShowCount(slug: string) {
  // Show count if showCount is enabled, the facet is active, and either:
  // 1. This facet is selected, or
  // 2. This is not a subcategory facet, or
  // 3. No other subcategory facet is selected
  return (
    searchWizardStore.showCount &&
    isFacetActive(slug) &&
    (props.selectedFacets.includes(slug) ||
      !isSubcategoryFacet(slug) ||
      !isAnotherSubcategoryFacetSelected(slug))
  )
}

function isFacetActive(slug: string) {
  // it's active if:
  // 1. We aren't showing counts at all, or
  // 2. It has a count value, or
  // 3. It's currently selected, or
  // 4. It's a subcategory facet (these should always be clickable)
  return (
    !searchWizardStore.showCount ||
    !!props.counts[slug] ||
    props.selectedFacets.includes(slug) ||
    isSubcategoryFacet(slug)
  )
}

const facets = computed(() => props.group.facets)
</script>
